import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import { getApi, postApi } from '../service/service';
import { API_URL } from '../config';
import CircularLoader from '../components/common/loader/loader';
import { toast } from '../utils/toast';
import { getToken, getUserRole, getUserLoginName } from '../utils/auth';
import Dropdown from '../utils/Dropdown';

class UsersList extends Component {
  state = {
    isLoading: true,
    users: [],
    editingUserId: null,
    editingUserRole: null
  };

  componentDidMount() {
    // Check if user is superadmin
    const role = getUserRole();
    if (role !== 'superadmin') {
      toast.error('Access denied. Only superadmin can view users.');
      this.props.history.push('/');
      return;
    }
    this.fetchUsers();
  }

  fetchUsers = () => {
    const token = getToken();
    if (!token) {
      toast.error('You must be logged in to perform this action.');
      this.props.history.push('/login');
      return;
    }

    this.setState({ isLoading: true });

    const header = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };

    getApi(`${API_URL}/api/auth/users`, header)
      .then((response) => {
        this.setState({ isLoading: false });
        if (response.status && response.users) {
          this.setState({ users: response.users });
        } else {
          const errorMessage = response.message || response.msg || 'Failed to fetch users.';
          toast.error(errorMessage);
        }
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        const errorMessage = error.message || error.msg || 'Failed to fetch users. Please try again.';
        toast.error(errorMessage);
        
        if (error.statusCode === 401 || error.statusCode === 403) {
          this.props.history.push('/login');
        }
      });
  };

  handleRoleChange = (userId, newRole) => {
    const token = getToken();
    const currentUserEmail = getUserLoginName();

    if (!token) {
      toast.error('You must be logged in to perform this action.');
      this.props.history.push('/login');
      return;
    }

    const header = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };

    const body = {
      userId: userId,
      role: newRole
    };

    // Use PUT method for update-role
    fetch(`${API_URL}/api/auth/update-role`, {
      method: 'PUT',
      headers: header,
      body: JSON.stringify(body)
    })
      .then(async (res) => {
        if (!res.ok) {
          let errorData;
          try {
            errorData = await res.json();
          } catch (e) {
            errorData = { status: false, msg: `HTTP ${res.status}: ${res.statusText}` };
          }
          throw {
            status: false,
            message: errorData.msg || errorData.message || errorData.error || `Request failed with status ${res.status}`,
            statusCode: res.status
          };
        }
        return res.json();
      })
      .then((data) => {
        if (data.status) {
          toast.success(data.message || 'User role updated successfully!');
          this.setState({ editingUserId: null, editingUserRole: null });
          this.fetchUsers(); // Refresh the list
        } else {
          const errorMessage = data.message || data.msg || 'Failed to update user role.';
          toast.error(errorMessage);
        }
      })
      .catch((error) => {
        const errorMessage = error.message || error.msg || 'An error occurred. Please try again.';
        toast.error(errorMessage);
        
        if (error.statusCode === 401 || error.statusCode === 403) {
          this.props.history.push('/login');
        }
      });
  };

  startEditRole = (userId, currentRole) => {
    this.setState({
      editingUserId: userId,
      editingUserRole: currentRole
    });
  };

  cancelEditRole = () => {
    this.setState({
      editingUserId: null,
      editingUserRole: null
    });
  };

  handleDeleteUser = (userId, userEmail) => {
    const currentUserEmail = getUserLoginName();

    // Prevent user from deleting themselves (check by email since we don't have user ID in localStorage)
    // We'll also check on backend, but this provides early feedback

    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    const token = getToken();
    if (!token) {
      toast.error('You must be logged in to perform this action.');
      this.props.history.push('/login');
      return;
    }

    const header = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };

    // Use DELETE method
    fetch(`${API_URL}/api/auth/user/${userId}`, {
      method: 'DELETE',
      headers: header
    })
      .then(async (res) => {
        if (!res.ok) {
          let errorData;
          try {
            errorData = await res.json();
          } catch (e) {
            errorData = { status: false, msg: `HTTP ${res.status}: ${res.statusText}` };
          }
          throw {
            status: false,
            message: errorData.msg || errorData.message || errorData.error || `Request failed with status ${res.status}`,
            statusCode: res.status
          };
        }
        return res.json();
      })
      .then((data) => {
        if (data.status) {
          toast.success(data.message || 'User deleted successfully!');
          this.fetchUsers(); // Refresh the list
        } else {
          const errorMessage = data.message || data.msg || 'Failed to delete user.';
          toast.error(errorMessage);
        }
      })
      .catch((error) => {
        const errorMessage = error.message || error.msg || 'An error occurred. Please try again.';
        toast.error(errorMessage);
        
        if (error.statusCode === 401 || error.statusCode === 403) {
          this.props.history.push('/login');
        }
      });
  };

  render() {
    const { isLoading, users, editingUserId, editingUserRole } = this.state;
    const currentUserEmail = getUserLoginName();

    const roleOptions = [
      { id: 'user', name: 'User' },
      { id: 'admin', name: 'Admin' },
      { id: 'superadmin', name: 'Super Admin' }
    ];

    const getRoleBadgeColor = (role) => {
      if (role === 'superadmin') return '#d9534f';
      if (role === 'admin') return '#5bc0de';
      return '#5cb85c';
    };

    return (
      <div style={{ minHeight: '700px' }}>
        <div className="jumbotron" style={{ marginBottom: '4%' }}></div>
        {isLoading ? (
          <div className="loader-container">
            <CircularLoader />
          </div>
        ) : (
          <div className="main-page-container" style={{ position: 'static', width: 'auto' }}>
            <h5>Users Management</h5>
            <div style={{ position: 'inherit', width: '90%', height: 'auto', maxWidth: '100%' }}>
              {users.length === 0 ? (
                <p>No users found.</p>
              ) : (
                <React.Fragment>
                  {/* Desktop Table View */}
                  <div className="users-table-desktop" style={{ display: 'block', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Role</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Created</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => {
                          const isCurrentUser = currentUserEmail && user.email === currentUserEmail;
                          const isEditing = editingUserId === user._id;

                          return (
                            <tr key={user._id} style={{ borderBottom: '1px solid #ddd' }}>
                              <td style={{ padding: '12px' }}>{user.name}</td>
                              <td style={{ padding: '12px' }}>{user.email}</td>
                              <td style={{ padding: '12px' }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <Dropdown
                                      options={roleOptions}
                                      value={editingUserRole}
                                      onChange={(e) => {
                                        this.setState({ editingUserRole: e.target.value });
                                      }}
                                      style={{ width: '150px' }}
                                    />
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => this.handleRoleChange(user._id, editingUserRole)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={this.cancelEditRole}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <span style={{ 
                                    padding: '4px 8px', 
                                    borderRadius: '4px',
                                    backgroundColor: getRoleBadgeColor(user.role),
                                    color: 'white',
                                    fontSize: '12px',
                                    display: 'inline-block'
                                  }}>
                                    {user.role}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '12px' }}>
                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                              </td>
                              <td style={{ padding: '12px' }}>
                                {!isEditing && !isCurrentUser && (
                                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => this.startEditRole(user._id, user.role)}
                                    >
                                      Edit Role
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => this.handleDeleteUser(user._id, user.email)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                )}
                                {isCurrentUser && (
                                  <span style={{ color: '#999', fontSize: '12px' }}>Current User</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="users-cards-mobile" style={{ display: 'none' }}>
                    {users.map((user) => {
                      const isCurrentUser = currentUserEmail && user.email === currentUserEmail;
                      const isEditing = editingUserId === user._id;

                      return (
                        <div 
                          key={user._id} 
                          style={{ 
                            border: '1px solid #ddd', 
                            borderRadius: '8px', 
                            padding: '15px', 
                            marginBottom: '15px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ fontSize: '16px', color: '#333' }}>{user.name}</strong>
                          </div>
                          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                            <strong>Email:</strong> {user.email}
                          </div>
                          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                            <strong>Role:</strong>{' '}
                            {isEditing ? (
                              <div style={{ marginTop: '10px' }}>
                                <Dropdown
                                  options={roleOptions}
                                  value={editingUserRole}
                                  onChange={(e) => {
                                    this.setState({ editingUserRole: e.target.value });
                                  }}
                                  style={{ width: '100%', marginBottom: '10px' }}
                                />
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => this.handleRoleChange(user._id, editingUserRole)}
                                    style={{ flex: 1 }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={this.cancelEditRole}
                                    style={{ flex: 1 }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <span style={{ 
                                padding: '4px 8px', 
                                borderRadius: '4px',
                                backgroundColor: getRoleBadgeColor(user.role),
                                color: 'white',
                                fontSize: '12px',
                                display: 'inline-block',
                                marginLeft: '5px'
                              }}>
                                {user.role}
                              </span>
                            )}
                          </div>
                          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                            <strong>Created:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                          </div>
                          {!isEditing && !isCurrentUser && (
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => this.startEditRole(user._id, user.role)}
                                style={{ flex: 1 }}
                              >
                                Edit Role
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => this.handleDeleteUser(user._id, user.email)}
                                style={{ flex: 1 }}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                          {isCurrentUser && (
                            <div style={{ marginTop: '10px', color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                              Current User
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        )}
        <style>{`
          @media only screen and (max-width: 768px) {
            .users-table-desktop {
              display: none !important;
            }
            .users-cards-mobile {
              display: block !important;
            }
          }
          @media only screen and (min-width: 769px) {
            .users-table-desktop {
              display: block !important;
            }
            .users-cards-mobile {
              display: none !important;
            }
          }
        `}</style>
      </div>
    );
  }
}

export default UsersList;




// WEBPACK FOOTER //
// ./src/pages/UsersList.jsx